import { useState, useEffect } from 'react';
import { Row, Col, Spin, Empty, Button, Input, Select, message, Modal } from 'antd';
import { PlusOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import PlanCard from './PlanCard';
import PlanDetailsModal from './PlanDetailsModel';
import { getAllPlans, deletePlan } from './PlansapiFunction';
import { toast } from 'react-toastify';
const { Option } = Select;

const PlanList = ({ onEdit, onAddNew, onAssign }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [planType, setPlanType] = useState('all');
  const [isActive, setIsActive] = useState('all');
  const [error, setError] = useState('');

  // Controls the "view all details" popup opened by clicking a card.
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    getAllPlans().then((res) => {
      if (res?.status === "success") {
        setPlans(res?.data?.plans || []);
        setLoading(false);
      } else {
        message.error(res?.message || 'Failed to fetch plans');
      }
    }).catch((err) => {
      console.log("error", err);
      setLoading(false);
    }).finally(() => {
      setLoading(false);
    });
  };
  useEffect(() => {
    fetchPlans();
  }, [planType, isActive, search]);

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Plan',
      content: 'Are you sure you want to delete this plan?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deletePlan(id);
          message.success('Plan deleted successfully');
          fetchPlans();
        } catch (error) {
          message.error('Failed to delete plan');
        }
      }
    });
  };

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    // small delay avoids the content flashing empty before the modal fades out
    setTimeout(() => setSelectedPlan(null), 200);
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold">Plan Management</h2>
          <p className="text-gray-500">Manage all subscription plans</p>
        </div>
        <div className='flex gap-2'>
          <button
            onClick={onAssign}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
          >
            + Assign Plan to Vendor
          </button>
          <button
            onClick={onAddNew}
            className="bg-blue-600 text-white rounded-lg px-4 py-2"
          >
           <PlusOutlined /> Create New Plan
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm mb-6 flex flex-wrap gap-4 items-center border border-gray-200">
        <Input
          placeholder="Search plans..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64"
          allowClear
        />
        <Select
          placeholder="Plan Type"
          value={planType}
          onChange={setPlanType}
          className="w-40"
        >
          <Option value="all">All Types</Option>
          <Option value="free">Free</Option>
          <Option value="paid">Paid</Option>
        </Select>
        <Select
          placeholder="Status"
          value={isActive}
          onChange={setIsActive}
          className="w-40"
        >
          <Option value="all">All Status</Option>
          <Option value="1">Active</Option>
          <Option value="0">Inactive</Option>
        </Select>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchPlans}
        >
          Refresh
        </Button>
        <span className="text-sm text-gray-500 ml-auto">
          {plans.length} plans found
        </span>
      </div>

      {/* Plans Grid */}
      {loading ? (
        <div className="text-center py-20">
          <Spin size="large" />
        </div>
      ) : error ? (
        <div className="text-center py-20">
          <div className="text-red-500 text-lg">{error}</div>
        </div>
      ) : plans.length === 0 ? (
        <Empty
          description="No plans found"
          className="py-20"
        />
      ) : (
        // align="stretch" makes every Col in a row match the tallest card;
        // combined with h-full flex-col inside PlanCard, this equalises heights.
        <Row gutter={[16, 16]} align="stretch">
          {plans.map((plan) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={plan.id} className="flex">
              <PlanCard
                plan={plan}
                onView={handleViewPlan}
                onEdit={onEdit}
                onDelete={handleDelete}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Full-detail popup, opened by clicking any card */}
      <PlanDetailsModal
        open={detailsOpen}
        plan={selectedPlan}
        onClose={handleCloseDetails}
        onEdit={onEdit}
      />
    </div>
  );
};

export default PlanList;